/**
 * Enum representing the different custom events that can be dispatched and listened for in Matters MFE.
 * These events are used to communicate between various parts of the application.
 */
export enum MattersEvents {
  SET_ACTIVE_CHAT = 'setActiveChat',
  SET_BREADCRUMBS = 'setBreadcrumbs',
  SET_STAGED_FILE_COLLECTION_ID = 'setStagedFileCollectionId',
  SET_MATTER_SETTINGS_STATE = 'setMatterSettingsState',
  SET_MATTER_FILE_VIEW_STATE = 'setMatterFileViewState',
  SET_SKILL_RESULT_STATE = 'setSkillResultState',
  SET_TEMPLATE_ID = 'setTemplateId',
  RESET_RESULT_PANEL = 'resetResultPanel',
  CLOSE_RESULT_PANEL = 'closeResultPanel',
  SKILL_RESULT_CLICKED = 'skillResult',
  OPEN_MATTER_FILES = 'openMatterFiles',
  SET_IS_RECENT_CHATS_VIEW = 'setIsRecentChatsView',
  PATHNAME_CHANGE = 'pathnameChange',
  SEND_INIT_STORE = 'sendInitialStore',
  GO_BACK = 'goBack',
  MATTER_CREATED = 'matterCreated',
  SET_MATTERS_HOME_STATE = 'setMattersHomeState',
  MATTERS_CONTAINER_NAVIGATION = 'mattersContainerNavigation',
  SET_HAS_RECENT_CHATS = 'setHasRecentChats',
  SET_MATTERS_SIDEBAR_READY = 'setMattersSidebarReady',
  REQUEST_MATTERS_SIDEBAR_STATUS = 'requestMattersSidebarStatus',
}

/**
 * Dispatches a custom Matters MFE event.
 *
 * This function allows you to trigger any of the predefined Matters events with optional details.
 * It is useful for broadcasting events across your Matters MFE application.
 *
 * @param {MattersEvents} eventName - The name of the event to dispatch (e.g., `MattersEvents.SET_ACTIVE_CHAT`).
 * @param {any} [detail] - Optional detail to pass along with the event.
 *
 * @example
 * dispatchMattersEvent(MattersEvents.SET_ACTIVE_CHAT, { newActiveChat: myChatItem });
 */
/* eslint-disable  @typescript-eslint/no-explicit-any */
export const dispatchMattersEvent = (eventName: MattersEvents, detail?: any) =>
  window.dispatchEvent(
    new CustomEvent(eventName, {
      detail,
    })
  );

/**
 * Listens for a specific Matters MFE event and runs the provided callback when the event is triggered.
 *
 * This function sets up an event listener for any of the predefined Matters events, allowing you to handle them in your application.
 *
 * @param {MattersEvents} event - The name of the event to listen for (e.g., `MattersEvents.SET_ACTIVE_FOLDER`).
 * @param {(e: CustomEvent) => void} callback - The function to call when the event is triggered.
 *
 * @example
 * listenForMattersEvent(MattersEvents.SET_ACTIVE_CHAT, (e) => {
 *   console.log('Active chat changed:', e.detail.newActiveChat);
 * });
 */
export const listenForMattersEvent = (event: MattersEvents, callback: (e: CustomEvent) => void): VoidFunction => {
  //@ts-expect-error Type 'Event' is missing the following properties from type 'CustomEvent<any>'
  window.addEventListener(event, callback);
  //@ts-expect-error Type 'Event' is missing the following properties from type 'CustomEvent<any>'
  return () => window.removeEventListener(event, callback);
};
